import React, { useEffect, useMemo, useState } from "react";
import * as XLSX from "@e965/xlsx";
import { getMessage } from "../data/locales";

/**
 * @typedef {{ name: string, rows: Array<Array<string | number | boolean>> }} SpreadsheetSheet
 */

export const SPREADSHEET_PREVIEW_MAX_ROWS = 500;

/**
 * @param {Array<Array<string | number | boolean>>} rows
 * @param {number} [maxRows]
 * @returns {{ visibleRows: Array<Array<string | number | boolean>>, totalRows: number, isTruncated: boolean }}
 */
export function getSpreadsheetPreviewRows(rows, maxRows = SPREADSHEET_PREVIEW_MAX_ROWS) {
  const allRows = Array.isArray(rows) ? rows : [];
  const limit = Number.isFinite(maxRows) && maxRows > 0 ? Math.floor(maxRows) : SPREADSHEET_PREVIEW_MAX_ROWS;
  const totalRows = allRows.length;
  if (totalRows <= limit) {
    return { visibleRows: allRows, totalRows, isTruncated: false };
  }
  return {
    visibleRows: allRows.slice(0, limit),
    totalRows,
    isTruncated: true,
  };
}

/**
 * @param {ArrayBuffer} buffer
 * @returns {SpreadsheetSheet[]}
 */
export function parseSpreadsheetWorkbook(buffer) {
  const workbook = XLSX.read(buffer, { type: "array" });
  return workbook.SheetNames.map((name) => ({
    name,
    rows: XLSX.utils.sheet_to_json(workbook.Sheets[name], {
      header: 1,
      defval: "",
      raw: false,
    }),
  }));
}

/**
 * @param {SpreadsheetSheet | null | undefined} sheet
 * @returns {number}
 */
export function getSpreadsheetColumnCountForSheet(sheet) {
  if (!sheet || !Array.isArray(sheet.rows)) {
    return 0;
  }
  return sheet.rows.reduce(
    (rowMax, row) => Math.max(rowMax, Array.isArray(row) ? row.length : 0),
    0,
  );
}

/**
 * @param {{ viewUrl: string }} props
 */
function CourseSpreadsheetPreview({ viewUrl }) {
  const [loadState, setLoadState] = useState("loading");
  /** @type {[SpreadsheetSheet[], React.Dispatch<React.SetStateAction<SpreadsheetSheet[]>>]} */
  const [sheets, setSheets] = useState([]);
  const [activeSheetIndex, setActiveSheetIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadWorkbook() {
      setLoadState("loading");
      setSheets([]);
      setActiveSheetIndex(0);

      try {
        const response = await fetch(viewUrl, { credentials: "include" });
        if (!response.ok) {
          throw new Error("spreadsheet fetch failed");
        }
        const buffer = await response.arrayBuffer();
        const parsedSheets = parseSpreadsheetWorkbook(buffer);
        if (cancelled) {
          return;
        }
        if (parsedSheets.length === 0) {
          setLoadState("empty");
          return;
        }
        setSheets(parsedSheets);
        setLoadState("ready");
      } catch (error) {
        console.error("Error loading spreadsheet preview:", error);
        if (!cancelled) {
          setLoadState("error");
        }
      }
    }

    loadWorkbook();
    return () => {
      cancelled = true;
    };
  }, [viewUrl]);

  const activeSheet = sheets[activeSheetIndex] ?? null;
  const columnCount = useMemo(
    () => getSpreadsheetColumnCountForSheet(activeSheet),
    [activeSheet],
  );
  const previewRows = useMemo(
    () => getSpreadsheetPreviewRows(activeSheet?.rows ?? []),
    [activeSheet],
  );

  if (loadState === "loading") {
    return (
      <p className="course-material-embed-status">
        {getMessage("campus.spreadsheetPreviewLoading")}
      </p>
    );
  }

  if (loadState === "error") {
    return (
      <p className="course-material-embed-error">
        {getMessage("campus.spreadsheetPreviewFailed")}
      </p>
    );
  }

  if (loadState === "empty" || !activeSheet) {
    return (
      <p className="course-material-embed-status">
        {getMessage("campus.spreadsheetPreviewEmpty")}
      </p>
    );
  }

  return (
    <div className="course-material-spreadsheet">
      {sheets.length > 1 && (
        <div className="course-material-spreadsheet-tabs" role="tablist" aria-label={getMessage("campus.spreadsheetSheetsLabel")}>
          {sheets.map((sheet, index) => (
            <button
              key={sheet.name}
              type="button"
              role="tab"
              aria-selected={index === activeSheetIndex}
              className={`course-material-spreadsheet-tab${index === activeSheetIndex ? " is-active" : ""}`}
              onClick={() => setActiveSheetIndex(index)}
            >
              {sheet.name}
            </button>
          ))}
        </div>
      )}
      {previewRows.isTruncated && (
        <p className="course-material-spreadsheet-truncated" role="status">
          {getMessage("campus.spreadsheetPreviewTruncated", {
            shown: previewRows.visibleRows.length,
            total: previewRows.totalRows,
          })}
        </p>
      )}
      <div className="course-material-spreadsheet-table-wrap">
        <table className="course-material-spreadsheet-table">
          <tbody>
            {previewRows.visibleRows.map((row, rowIndex) => (
              <tr key={`${activeSheet.name}-${rowIndex}`}>
                {Array.from({ length: columnCount }, (_, cellIndex) => {
                  const cell = Array.isArray(row) ? row[cellIndex] : "";
                  return (
                    <td key={`${activeSheet.name}-${rowIndex}-${cellIndex}`}>
                      {cell == null ? "" : String(cell)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CourseSpreadsheetPreview;
